import React from 'react';
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import { getCareSourceSavedRecordCount } from './careSourceStatusModel';

const ReceiptGroup = ({ title, count = 0, items = [], href = '' }) => {
  if (!count && !items.length) return null;
  const heading = (
    <Typography sx={{ color: colors.semantic.ink, fontSize: '0.84rem', fontWeight: 700 }}>
      {title} · {count || items.length}
    </Typography>
  );

  return (
    <Box>
      {href ? (
        <ButtonBase
          component="a"
          href={href}
          aria-label={`Open ${title}`}
          sx={{ width: '100%', justifyContent: 'space-between', borderRadius: '8px', py: 0.25, textAlign: 'left' }}
        >
          {heading}
          <ChevronRightRoundedIcon aria-hidden="true" sx={{ color: colors.semantic.sageDeep, fontSize: 20 }} />
        </ButtonBase>
      ) : heading}
      {items.map((item) => (
        <Typography key={`${item.topicKey}-${item.title}`} sx={{ mt: 0.3, color: alpha(colors.semantic.ink, 0.72), fontSize: '0.8rem', lineHeight: 1.4 }}>
          {item.targetDateText ? `${item.targetDateText} — ` : ''}{item.title}{item.tentative ? ' · Tentative' : ''}
        </Typography>
      ))}
    </Box>
  );
};

const EmailOrganizationReceipt = ({ sources = [], childId = '' }) => {
  const emailSources = sources.filter((source) => (
    source.sourceType === 'email_forward'
    || source.sourceMetadata?.inputMode === 'email_forward'
    || source.metadata?.inputMode === 'email_forward'
    || source.metadata?.careSourceProcessing?.inputMode === 'email_forward'
    || source.emailPlanSummary
  ));
  if (!emailSources.length) return null;

  const timelineCount = emailSources.reduce((total, source) => total + getCareSourceSavedRecordCount(source), 0);
  const addedPlans = emailSources.flatMap((source) => source.emailPlanSummary?.addedPlans || []);
  const reviewPlans = emailSources.flatMap((source) => source.emailPlanSummary?.reviewPlans || []);
  const needsDateItems = emailSources.flatMap((source) => source.emailTimelineSummary?.needsDateCandidates || []);
  const usefulCount = timelineCount + addedPlans.length + reviewPlans.length;
  const plansHref = childId ? `/children/${childId}/plans` : '';

  return (
    <Box sx={{ py: 1.25 }}>
      <Typography sx={{ color: colors.semantic.ink, fontSize: '0.9rem', fontWeight: 700 }}>
        We found {usefulCount} useful update{usefulCount === 1 ? '' : 's'} in this email
      </Typography>
      <Typography sx={{ mt: 0.2, color: alpha(colors.semantic.ink, 0.58), fontSize: '0.76rem' }}>
        Clear updates were added automatically. Consequential changes still need review.
      </Typography>
      <Stack spacing={1} divider={<Divider flexItem />} sx={{ mt: 1.1 }}>
        <ReceiptGroup title="Timeline events" count={timelineCount} />
        <ReceiptGroup title="Plans & next steps" items={addedPlans} href={plansHref} />
        <ReceiptGroup title="Plan updates to review" items={reviewPlans} href={plansHref} />
        <ReceiptGroup title="Updates needing a date" items={needsDateItems} />
      </Stack>
    </Box>
  );
};

export default EmailOrganizationReceipt;
